import BookmarkOutlinedIcon from '@mui/icons-material/BookmarkOutlined';
import type { Theme } from '@mui/material';
import { Grid2 as Grid, Tooltip, Typography } from '@mui/material';
import { memo, useCallback } from 'react';
import { Badge } from '@/pages/ValuationsV2/components/Badge';
import { UpdatedAt } from '@/pages/ValuationsV2/components/UpdatedAt';
import { useValuationsStore } from '@/pages/ValuationsV2/store/valuationsStore';
import type { Asset } from '@/types/GraphqlSchema';
import { sxVariants } from '@/utils/combineSx';
import { SORT_FILTER_TO_METRIC } from './constants';

const assetListItemStyles = {
  container: (theme: Theme) => ({
    position: 'relative',
    borderBottom: `1px solid ${theme.palette.baseColors.iceBlue.c100}`,
    padding: '0.5rem 1rem',
    transition: 'background-color 0.2s ease',
    gap: '0.32rem',
    cursor: 'pointer',
    backgroundColor: theme.palette.baseColors.white.c100,
    borderLeft: '0.25rem solid transparent',
    paddingLeft: '0.75rem',
    overflow: 'hidden',
    scrollSnapAlign: 'start',
    '&:hover': {
      backgroundColor: theme.palette.baseColors.cloudGray.c50,
    },
  }),
  containerSelected: (theme: Theme) => ({
    borderLeftColor: theme.palette.actionCustom.backgroundColor,
  }),
  countryText: (theme: Theme) => ({
    fontSize: theme.fontSizes.smallest,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  }),
  watchList: (theme: Theme) => ({
    position: 'absolute',
    top: 'calc((0.25rem + 1px) * -1)',
    right: 0,
    fill: theme.palette.baseColors.iceBlue.c100,
  }),
};

type AssetListItemContainerProps = {
  children: React.ReactNode;
  asset: Asset;
};

type AssetListItemInfoProps = {
  asset: Asset;
};

const containerStyles = sxVariants({
  base: assetListItemStyles.container,
  variants: {
    isSelected: {
      true: assetListItemStyles.containerSelected,
    },
  },
});

const AssetListItemContainer = ({ children, asset }: AssetListItemContainerProps) => {
  const selectedAsset = useValuationsStore.use.selectedAsset();
  const setSelectedAsset = useValuationsStore.use.setSelectedAsset();

  const handleAssetClick = useCallback(() => {
    setSelectedAsset(asset);
  }, [setSelectedAsset, asset]);

  const isSelected = selectedAsset?.id === asset.id;

  return (
    <Grid
      container
      onClick={handleAssetClick}
      direction='column'
      sx={containerStyles({ isSelected })}
      data-id='asset-list-item'
    >
      {children}
    </Grid>
  );
};

const WatchListMark = ({ asset }: AssetListItemInfoProps) => {
  const isInWatchList = useValuationsStore.use.watchlistAssetIds().has(asset.id);

  if (!isInWatchList) {
    return null;
  }

  return (
    <Tooltip title={<Typography variant='overline'>Asset on Watch List</Typography>}>
      <BookmarkOutlinedIcon sx={assetListItemStyles.watchList} />
    </Tooltip>
  );
};

const AssetListItemInfo = memo(({ asset }: AssetListItemInfoProps) => {
  return (
    <Grid display='flex' flexDirection='column' gap={0.5} data-id='asset-list-item-info'>
      <WatchListMark asset={asset} />
      <Typography
        variant='caption'
        color='text.secondary'
        fontWeight={600}
        sx={assetListItemStyles.countryText}
        data-id='asset-list-item-country'
      >
        {asset.country.displayName}
      </Typography>

      <Typography
        color='secondary'
        variant='subtitle1'
        fontWeight={700}
        lineHeight={1.3}
        data-id='asset-list-item-name'
      >
        {asset.displayName}
      </Typography>
      <UpdatedAt
        date={asset.reportUpdatedDate}
        sx={{
          position: 'absolute',
          inset: 'auto 0.5rem 0.25rem auto',
        }}
        prefix=''
        format={{
          year: 'numeric',
          month: 'short',
        }}
      />
    </Grid>
  );
});

type AssetListItemBadgesProps = {
  asset: Asset;
  sortType: { filter: string };
};

const AssetListItemBadges = memo(({ asset, sortType }: AssetListItemBadgesProps) => {
  const metricConfig = SORT_FILTER_TO_METRIC[sortType.filter as keyof typeof SORT_FILTER_TO_METRIC];
  const metricValue = metricConfig ? asset[metricConfig.field] : null;

  return (
    <Grid display='flex' gap={0.5} alignItems='center' data-id='asset-list-item-badges'>
      {asset.mAndAOpportunity && (
        <Badge display='flex' variant='ma' data-id='asset-list-item-ma-badge'>
          M&A
        </Badge>
      )}
      {metricValue ? (
        <Badge
          display='flex'
          alignItems='center'
          justifyContent='center'
          flexDirection='row'
          gap={0.25}
          data-id='asset-list-item-metric'
        >
          {metricConfig.label} {metricConfig.unit}:
          <b data-id='asset-list-item-metric-value'>{metricValue}</b>
        </Badge>
      ) : null}
    </Grid>
  );
});

type AssetListItemProps = {
  asset: Asset;
  children: React.ReactNode;
};

const AssetListItemRoot = memo(({ asset, children }: AssetListItemProps) => {
  return <AssetListItemContainer asset={asset}>{children}</AssetListItemContainer>;
});

const AssetListItem = {
  Root: AssetListItemRoot,
  Info: AssetListItemInfo,
  AssetListItemBadges: AssetListItemBadges,
};

export { AssetListItem };
